<app-navigation-header title="Access Report" i18n-title="@@accessReport-accessReportTitle">
	<app-data-set
		#dataSet
		[dataSource]="dataSource"
		defaultPresentationMode="table"
		[appBreadcrumbLabel]="breadcrumbLabel"
		[rowId]="rowId"
		firstColumnProperty="deviceSerialNumber"
	>
		<app-data-set-toolbar>
			<div class="flex-space-around">
				<div *appActionGroup style="width: 100%; display: flex; flex-direction: column">
					<app-data-set-action-button
						style="flex: 1"
						label="Refine Filters"
						i18n-label="@@accessReport-refineFilters"
						svgIcon="sl-filter"
						data-automation-id="access-report-refine-filters-button"
						(click)="onRefineFiltersClicked()"
					></app-data-set-action-button>
				</div>
				<div *appActionGroup style="width: 100%; display: flex; flex-direction: column">
					<span
						class="start-export-button"
						[class.start-export-button-disabled]="exportHasNoRows"
						[matTooltip]="exportNoRecordsTooltip"
						[matTooltipDisabled]="!exportHasNoRows"
						matTooltipPosition="above"
					>
						<app-data-set-action-button
							style="flex: 1"
							label="Start Export"
							i18n-label="@@accessReport-downloadReport"
							svgIcon="sl-download"
							data-automation-id="access-report-download-button"
							[isDisabled]="startExportDisabled"
							(click)="onDownloadClicked()"
						></app-data-set-action-button>
					</span>
				</div>
			</div>
		</app-data-set-toolbar>

		<app-data-set-content>
			@if (exportStillRunning && (exportStatus?.status === 'preparing' || exportStatus?.status === 'waiting')) {
				<div
					class="export-status export-status-callout"
					data-automation-id="access-report-export-still-running"
					i18n="@@accessReport-exportStillRunning"
				>
					Your export is still in progress. Please wait until that has completed before starting another.
				</div>
			}
			@if (exportStillRunning && exportStatus?.status === 'none' && exportStatus?.companyName; as companyName) {
				<div
					class="export-status export-status-callout"
					data-automation-id="access-report-export-other-company"
					i18n="@@accessReport-exportOtherCompany"
				>
					You are currently exporting a report for {{ companyName }}. Please wait until that export has completed before
					starting another.
				</div>
			}
			@switch (exportStatus?.status) {
				@case ('waiting') {
					<div
						class="export-status export-status-callout"
						[class.export-status-callout-white]="exportStillRunning"
						data-automation-id="access-report-export-waiting"
						i18n="@@accessReport-exportWaiting"
					>
						We have queued your report export, this page will update once the download is ready. Your download will
						remain on your "Reports" page for {{ exportStatus?.companyName }} for 24 hours, or until you start another
						export.
					</div>
				}
				@case ('preparing') {
					<div
						class="export-status export-status-callout"
						[class.export-status-callout-white]="exportStillRunning"
						data-automation-id="access-report-export-preparing"
						i18n="@@accessReport-exportPreparing"
					>
						Your export is being prepared. This page will update once the download is ready. Your download will remain
						on your "Reports" page for {{ exportStatus?.companyName }} for 24 hours, or until you start another export.
					</div>
				}
				@case ('ready') {
					<div class="export-status export-status-callout" data-automation-id="access-report-export-ready">
						<span i18n="@@accessReport-exportReady">
							Your export is ready. Available to download for 24 hours. A new export will replace this one.
						</span>
						<button
							mat-stroked-button
							type="button"
							class="export-status-download"
							data-automation-id="access-report-export-download-button"
							[disabled]="loadingService.isLoading(downloadAccessReportExportLoadingKey)"
							(click)="onDownloadExportClicked()"
							i18n="@@accessReport-exportDownloadCsv"
						>
							Download CSV
						</button>
					</div>
				}
				@case ('failed') {
					<div
						class="export-status export-status-callout"
						data-automation-id="access-report-export-failed"
						i18n="@@accessReport-exportFailed"
					>
						Your export failed and was not saved. Click Start Export to start a new one.
					</div>
				}
			}
			<app-data-set-table-view>
				@if (loadingService.isLoading(generateAccessReportLoadingKey) || !finishedLoadingInitData) {
					<app-data-set-loading-row />
				}
				<table
					mat-table
					[dataSource]="dataSource"
					matSortActive="visitStartDate"
					matSortDirection="desc"
					matSort
					[hidden]="loadingService.isLoading(generateAccessReportLoadingKey) || !finishedLoadingInitData"
				>
					<tr class="mat-row" *matNoDataRow>
						<td class="mat-cell" [colSpan]="displayedColumns.length" i18n="@@dataSetTable-noRecordsFound">
							No records found.
						</td>
					</tr>

					<ng-container matColumnDef="deviceSerialNumber">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-serialNumber">Serial Number</th>
						<td mat-cell *matCellDef="let row">{{ row.deviceSerialNumber }}</td>
					</ng-container>

					<ng-container matColumnDef="deviceOwnerCompanyName">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-deviceOwner">Device Owner</th>
						<td mat-cell *matCellDef="let row">{{ row.deviceOwnerCompanyName }}</td>
					</ng-container>

					<ng-container matColumnDef="accessedByCompanyName">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-accessedByCompany">
							Accessed by Company
						</th>
						<td mat-cell *matCellDef="let row">{{ row.accessedByCompanyName }}</td>
					</ng-container>

					<ng-container matColumnDef="accessedByUserName">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-accessedByUser">
							Accessed by User
						</th>
						<td mat-cell *matCellDef="let row">{{ row.accessedByUserFirstName }} {{ row.accessedByUserLastName }}</td>
					</ng-container>

					@if (isCareCompany) {
						<ng-container matColumnDef="clientName">
							<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@inventoryReport-client">Client</th>
							<td mat-cell *matCellDef="let row">{{ row?.clientFirstName ?? '' }} {{ row?.clientLastName ?? '' }}</td>
						</ng-container>

						<ng-container matColumnDef="clientIdn">
							<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@inventoryReport-clientIdn">Client IDN</th>
							<td mat-cell *matCellDef="let row">{{ row?.clientIdn ?? '' }}</td>
						</ng-container>
					}

					<ng-container matColumnDef="propertyName">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-propertyName">Property Name</th>
						<td mat-cell *matCellDef="let row">{{ row.propertyName }}</td>
					</ng-container>

					<ng-container matColumnDef="deviceLocation">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-deviceLocation">
							Device Location
						</th>
						<td mat-cell *matCellDef="let row">{{ row.deviceLocation }}</td>
					</ng-container>

					<ng-container matColumnDef="address">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-address">Address</th>
						<td mat-cell *matCellDef="let row">
							<div [innerHtml]="row.address | address"></div>
						</td>
					</ng-container>

					<ng-container matColumnDef="visitStartDate">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-startDate">Visit Start Date</th>
						<td mat-cell *matCellDef="let row">
							<div class="date-time-container">
								<div>
									@if (row.visitStartDate) {
										<span>
											{{
												row.visitStartDate
													| date: 'mediumDate' : timeZoneService.utcOffset(row.visitStartDate, row.locationTimezoneName)
											}}
											<br />
											{{
												(row.visitStartDate
													| date
														: 'shortTime'
														: timeZoneService.utcOffset(row.visitStartDate, row.locationTimezoneName)) +
													' ' +
													(row.locationTimezoneName | abbreviatedTimezone: row.visitStartDate)
											}}
										</span>
									}
									@if (!row.visitStartDate) {
										<span>- - -</span>
									}
								</div>
								@if (row.alarmStart === AlarmStatus.disarm) {
									<img
										src="assets/icons/icon-shield-with-x.svg"
										class="ajax-icon"
										[alt]="disarmedAlarmText"
										[title]="disarmedAlarmText"
									/>
								}
								@if (row.alarmStart === AlarmStatus.overriden) {
									<img
										src="assets/icons/icon-bell-crossed-out-orange.svg"
										class="ajax-icon bell-icon"
										[alt]="alarmOverrideText"
										[title]="alarmOverrideText"
									/>
								}
							</div>
						</td>
					</ng-container>

					<ng-container matColumnDef="visitEndDate">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-endDate">Visit End Date</th>
						<td mat-cell *matCellDef="let row">
							<div class="date-time-container">
								<div>
									@if (row.visitEndDate) {
										<span>
											{{
												row.visitEndDate
													| date: 'mediumDate' : timeZoneService.utcOffset(row.visitEndDate, row.locationTimezoneName)
											}}
											<br />
											{{
												(row.visitEndDate
													| date: 'shortTime' : timeZoneService.utcOffset(row.visitEndDate, row.locationTimezoneName)) +
													' ' +
													(row.locationTimezoneName | abbreviatedTimezone: row.visitEndDate)
											}}
										</span>
									}
									@if (!row.visitEndDate) {
										<span>- - -</span>
									}
								</div>
								@if (row.alarmEnd === AlarmStatus.arm) {
									<img
										src="assets/icons/icon-shield-checkmark-red.svg"
										class="ajax-icon"
										[alt]="armedAlarmText"
										[title]="armedAlarmText"
									/>
								}
							</div>
						</td>
					</ng-container>

					<ng-container matColumnDef="accessType">
						<th mat-header-cell *matHeaderCellDef mat-sort-header i18n="@@accessReport-accessType">Access Type</th>
						<td mat-cell *matCellDef="let row" [attr.data-automation-id]="'access-report-access-type-' + rowId(row)">
							{{ row.accessType | accessTypeFormat }}
						</td>
					</ng-container>

					<tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
					<tr
						mat-row
						[ngClass]="{ 'code-visit': row.accessType === 'Code' }"
						*matRowDef="let row; columns: displayedColumns"
					></tr>
				</table>
			</app-data-set-table-view>
		</app-data-set-content>
	</app-data-set>
</app-navigation-header>
